import React, { useEffect, useMemo, useRef, useState } from "react";
import moment, { Moment } from "moment";
import ShopDTO from "@/dto/shop/ShopDTO";
import ShopType from "@/enum/ShopType";
import { useGlobalStore } from "@/store/hooks";
import { observer } from "mobx-react-lite";

interface ShippingDeadlineCalculatorProps {
    shop: ShopDTO;
    shippingDeadline: string;
    showCountdown: boolean;
}

type CurrentTimeListener = (currentTime: Moment) => void;

// 订单行会大量挂载倒计时组件，所有实例共用一个分钟级 timer，避免每行各自维护 setTimeout。
const currentTimeListeners = new Set<CurrentTimeListener>();
let currentTimeTimer: ReturnType<typeof setTimeout> | null = null;

function notifyCurrentTimeListeners() {
    const currentTime = moment();

    currentTimeListeners.forEach((listener) => {
        listener(currentTime);
    });
}

function scheduleCurrentTimeTick() {
    if (currentTimeTimer) {
        return;
    }

    currentTimeTimer = setTimeout(() => {
        currentTimeTimer = null;

        notifyCurrentTimeListeners();

        if (currentTimeListeners.size) {
            scheduleCurrentTimeTick();
        }
    }, 60000);
}

function subscribeCurrentTime(listener: CurrentTimeListener) {
    currentTimeListeners.add(listener);
    scheduleCurrentTimeTick();

    return () => {
        currentTimeListeners.delete(listener);

        if (!currentTimeListeners.size && currentTimeTimer) {
            clearTimeout(currentTimeTimer);
            currentTimeTimer = null;
        }
    };
}

function useSharedCurrentTime(enabled: boolean, deadlineTime?: Moment | null) {
    const [currentTime, setCurrentTime] = useState(() => moment());
    const hasMountedRef = useRef(false);
    const deadlineTimestamp = deadlineTime?.valueOf();
    const shouldSubscribe = enabled && (!deadlineTime || currentTime.isBefore(deadlineTime));

    useEffect(() => {
        if (!hasMountedRef.current) {
            hasMountedRef.current = true;

            return;
        }

        setCurrentTime(moment());
    }, [enabled, deadlineTimestamp]);

    useEffect(() => {
        if (!shouldSubscribe) {
            return;
        }

        return subscribeCurrentTime(setCurrentTime);
    }, [shouldSubscribe]);

    return currentTime;
}

function calculateTimeDifference(shippingDeadlineTime: Moment, currentTime: Moment) {
    try {
        const duration = moment.duration(shippingDeadlineTime.diff(currentTime));
        return {
            days: Math.floor(duration.asDays()),
            hours: duration.hours(),
            minutes: duration.minutes(),
            seconds: duration.seconds(),
        };
    } catch {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }
}

const ShippingDeadlineCalculator: React.FC<ShippingDeadlineCalculatorProps> = (props) => {
    const { shop, shippingDeadline, showCountdown } = props;
    const globalStore = useGlobalStore();
    const shopSetting = globalStore.shopSettingMapping.get(shop.id);
    const shippingDeadlineTime = useMemo(() => (shippingDeadline ? moment(shippingDeadline) : null), [
        shippingDeadline,
    ]);
    const currentTime = useSharedCurrentTime(!!shippingDeadline && !!shopSetting, shippingDeadlineTime);

    if (!shippingDeadline || !shopSetting || !shippingDeadlineTime) {
        return null;
    }

    const shippingDeadlineWarnHours = shopSetting.shippingDeadlineWarnHours;

    if (currentTime.isAfter(shippingDeadlineTime)) {
        if (showCountdown) {
            return <span className="danger fb">已超时({shippingDeadline})</span>;
        }

        return <span className="danger fb">{shippingDeadline}</span>;
    }

    const { days, hours, minutes } = calculateTimeDifference(shippingDeadlineTime, currentTime);
    const diff = `${days}天${hours}时${minutes}分`;

    const shippingDeadlineWarnTime = currentTime.clone().add(shippingDeadlineWarnHours || 0, "h");
    const isWarning = shippingDeadlineWarnTime.isAfter(shippingDeadlineTime);
    const classNames = isWarning ? "error fb" : undefined;

    const isPdd = [ShopType.PINDUODUO, ShopType.PDD_FDS].includes(shop.type);

    if (showCountdown) {
        return (
            <span className={classNames}>
                {isPdd ? `${diff}将超时揽收` : diff}({shippingDeadline})
            </span>
        );
    } else {
        return <span className={classNames}>{shippingDeadline}</span>;
    }
};

export default observer(ShippingDeadlineCalculator);
